// Guarda de isolamento do cache de catálogo entre CLIENTES (P0).
//
// Cenário que isto fecha: TV compartilhada em casa. Usuário A (provedor P1)
// usa o app e sai; B pareia P2 no mesmo aparelho e recebe a grade de P1.
//
// A causa estrutural foi removida no `sw.ts` (não existe mais rota de cache
// sobre `/v1/catalog/` — ver o bloco de decisão lá). Este módulo é a
// SEGUNDA camada, e ela não é redundante:
//
//   1. **Janela de troca de Service Worker.** Depois do deploy do fix, o SW
//      ANTIGO (com a rota de SWR) continua controlando a aba até ativar o
//      novo. Nesse intervalo o balde `iptv-catalog-*` velho ainda existe e
//      ainda é servido. `caches.delete()` daqui roda no contexto da PÁGINA,
//      então apaga o balde mesmo enquanto o SW velho manda.
//
//   2. **Quota.** Em device apertado, balde órfão ocupa espaço até o
//      `cleanupStaleCaches()` do SW novo passar.
//
// Por que um subscribe único em vez de chamar no botão de sair: o buraco
// original foi exatamente esse — `logout()` no `store.ts` é um `set()` puro,
// sem efeito nenhum, e existem VÁRIOS caminhos de logout (bootstrap
// `setOnAuthInvalid`, AuthGuard proativo por `account_expired`, botão do
// Settings, MobileProfile). Wirar N call sites garante esquecer o próximo
// que aparecer. Um subscribe é choke point único e pega o caminho novo de
// graça.
//
// Mesmo padrão de `registerSettingsScopeSync()` (settings-store.ts).

import { useSessionStore } from './store'

/** Prefixo dos baldes de catálogo do SW. O nome real leva o build hash
 *  no fim (`iptv-catalog-<hash>`), então casamos por prefixo pra pegar
 *  TODOS os builds — inclusive os órfãos de versões anteriores. */
export const CATALOG_CACHE_PREFIX = 'iptv-catalog'

/**
 * Um nome de Cache Storage é balde de catálogo?
 *
 * Fonte única desta regra — importada pelo runtime E pelos testes. Não
 * replique esta função em arquivo de teste: regra replicada fica verde
 * mesmo quando o runtime diverge (foi assim que o buraco passou batido).
 */
export function isCatalogCache(name: string): boolean {
  return name === CATALOG_CACHE_PREFIX || name.startsWith(`${CATALOG_CACHE_PREFIX}-`)
}

/** Subset de `CacheStorage` que usamos. Injetável pra teste. */
export interface CacheStorageLike {
  keys(): Promise<string[]>
  delete(name: string): Promise<boolean>
}

/**
 * Apaga TODOS os baldes de catálogo do Cache Storage. Retorna quantos
 * baldes foram removidos.
 *
 * Best-effort por decisão: nunca pode derrubar o fluxo de logout. Se o
 * Cache Storage não existir (`file://` do .ipk, onde não há SW) vira no-op.
 */
export async function purgeCatalogCaches(storage?: CacheStorageLike): Promise<number> {
  const caches = storage ?? (globalThis as { caches?: CacheStorageLike }).caches
  if (!caches?.keys) return 0
  try {
    const names = await caches.keys()
    const targets = names.filter(isCatalogCache)
    await Promise.all(targets.map((n) => caches.delete(n).catch(() => false)))
    return targets.length
  } catch (err) {
    console.warn('[catalog-cache-guard] purge falhou', err)
    return 0
  }
}

/** Identidade do provedor no estado da sessão. `null` = sem provedor. */
function providerKeyOf(s: {
  providerCode: string | null
  direct: { listName: string } | null
}): string | null {
  if (s.providerCode) return `p:${s.providerCode}`
  if (s.direct) return `d:${s.direct.listName}`
  return null
}

let _registered = false

/**
 * Liga a purga do catálogo nas duas transições que importam:
 *
 *   a) **Logout explícito** — `isAuthed` true → false. Cobre todo caminho
 *      de saída, inclusive `session_expired` / `account_expired`.
 *   b) **Troca de provedor SEM logout** — a identidade do provedor muda
 *      (código novo, lista direta diferente, ou login direto em outro
 *      provedor sem passar por logout).
 *
 * NÃO purga em troca de PERFIL dentro do mesmo provedor: catálogo é
 * compartilhado entre perfis do mesmo provedor (mesma doutrina de
 * `scope.ts`, onde `snapshotKey()` é provider-only). Purgar ali seria
 * custo de rede sem ganho de isolamento.
 *
 * Idempotente.
 */
export function registerCatalogCacheGuard(): void {
  if (_registered) return
  _registered = true

  useSessionStore.subscribe((state, prev) => {
    const loggedOut = prev.isAuthed && !state.isAuthed
    const providerChanged = providerKeyOf(prev) !== providerKeyOf(state)
    if (!loggedOut && !providerChanged) return
    void purgeCatalogCaches()
  })
}

/** Só pra teste: zera o flag de registro (o subscribe do zustand é
 *  module-level e sobrevive entre casos sem isto). */
export function __resetCatalogCacheGuardForTests(): void {
  _registered = false
}
